// ===========================================
// Custom Email Editor Page
// ===========================================
// Admin interface for creating and editing custom email campaigns

import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { useTenant } from '../../contexts/TenantContext';
import {
  Box,
  Paper,
  TextField,
  Button,
  Stack,
  Alert,
  CircularProgress,
  FormControlLabel,
  Switch,
  Autocomplete,
  Chip,
  Divider,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  IconButton,
  Tooltip,
  LinearProgress,
} from '@mui/material';
import {
  Save as SaveIcon,
  ContentCopy as DuplicateIcon,
  Analytics as AnalyticsIcon,
  Send as SendIcon,
  Schedule as ScheduleIcon,
  ArrowBack as BackIcon,
  Email as TestEmailIcon,
  People as PeopleIcon,
  AttachFile as AttachFileIcon,
  Delete as DeleteIcon,
  InsertDriveFile as FileIcon,
} from '@mui/icons-material';
import PageHeader from '../../components/common/PageHeader';
import RichTextEditor, { RichTextEditorHandle } from '../../components/common/RichTextEditor';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import MergeFieldChips from '../../components/common/MergeFieldChips';
import { insertAtSelectionEnd, isDuplicateMouseActivation } from '../../utils/mergeFieldInsert';
import {
  useCustomEmail,
  useCreateCustomEmail,
  useUpdateCustomEmail,
  useScheduleCustomEmail,
  useSendCustomEmail,
  useDuplicateCustomEmail,
  useSendTestEmail,
  useRecipientPreview,
  useAttachments,
  useUploadAttachment,
  useDeleteAttachment,
} from '../../hooks/useCustomEmail';
import {
  useAgeGroups,
  useProgramLevels,
  useInstruments,
} from '../../hooks/useAdmin';
import { useLessons } from '../../hooks/useLessons';
import { isCustomEmailDirty } from './customEmailDirty';
import { apiErrorMessage as saveErrorMessage } from '../../utils/apiError';
import { useUnsavedChangesWarning } from '../../hooks/useUnsavedChangesWarning';
import { useFamilies } from '../../hooks/useUsers';
import {
  CreateCustomEmailData,
  UpdateCustomEmailData,
  CustomEmailStatus,
} from '../../types/customEmail.types';

// ===========================================
// SCHEDULE DIALOG
// ===========================================

interface ScheduleDialogProps {
  open: boolean;
  onClose: () => void;
  onSchedule: (date: string) => void;
  isScheduling: boolean;
}

function ScheduleDialog({ open, onClose, onSchedule, isScheduling }: ScheduleDialogProps) {
  const [scheduledFor, setScheduledFor] = useState('');

  const handleSchedule = () => {
    if (scheduledFor) {
      onSchedule(new Date(scheduledFor).toISOString());
    }
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>Schedule Email</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Choose when to send this email campaign. Make sure to save any changes first.
        </Typography>
        <TextField
          label="Schedule For"
          type="datetime-local"
          value={scheduledFor}
          onChange={(e) => setScheduledFor(e.target.value)}
          fullWidth
          InputLabelProps={{ shrink: true }}
          inputProps={{ min: new Date().toISOString().slice(0, 16) }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isScheduling}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSchedule}
          disabled={isScheduling || !scheduledFor}
        >
          {isScheduling ? <CircularProgress size={20} /> : 'Schedule'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ===========================================
// TEST EMAIL DIALOG
// ===========================================

interface TestEmailDialogProps {
  open: boolean;
  onClose: () => void;
  onSend: (email: string) => void;
  isSending: boolean;
  defaultEmail: string;
}

function TestEmailDialog({ open, onClose, onSend, isSending, defaultEmail }: TestEmailDialogProps) {
  const [testEmail, setTestEmail] = useState(defaultEmail);

  useEffect(() => {
    setTestEmail(defaultEmail);
  }, [defaultEmail]);

  const handleSend = () => {
    if (testEmail) {
      onSend(testEmail);
    }
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>Send Test Email</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Send a preview of this email to test how it will appear to recipients.
        </Typography>
        <TextField
          label="Email Address"
          type="email"
          value={testEmail}
          onChange={(e) => setTestEmail(e.target.value)}
          fullWidth
          placeholder="Enter email address"
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isSending}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSend}
          disabled={isSending || !testEmail}
        >
          {isSending ? <CircularProgress size={20} /> : 'Send Test'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ===========================================
// RECIPIENT PREVIEW DIALOG
// ===========================================

/**
 * The merge fields an admin may insert.
 *
 * ⚠️ Must stay in step with `CUSTOM_EMAIL_MERGE_FIELDS` + `MERGE_FIELD_ALIASES`
 * in the backend's `customEmailMergeFields.ts`, which is the authority — it
 * rejects the save with a 422 naming anything it does not recognise.
 *
 * `{{parentName}}` is deliberately the FIRST name chip even though `firstName`
 * is the canonical field: it is what MNM already type (10 of their 12
 * token-carrying campaigns), because it is the placeholder every transactional
 * template uses. Offering the name they already know beats teaching a new one.
 *
 * ⚠️ `{{studentName}}` is NOT offered. A campaign goes to parents, and a parent
 * may have three enrolled children — there is no single student for a recipient.
 */
const MERGE_FIELDS = [
  { label: 'First name', placeholder: '{{parentName}}' },
  { label: 'Last name', placeholder: '{{lastName}}' },
  { label: 'School name', placeholder: '{{schoolName}}' },
];

// ⚠️ The chips themselves now live in `components/common/MergeFieldChips` —
// extracted 2026-09-17 when the email-sequence editor needed the same row with
// a different field list. `MERGE_FIELDS` stays HERE on purpose: the list above
// is tied to the custom-email backend authority, which refuses
// `{{studentName}}`, and sequences offer it. Sharing the component is right;
// sharing the list would eventually be wrong for one of them.

/**
 * Catch a bad token BEFORE the round trip.
 *
 * The backend is the authority and rejects with a 422 — this only saves the
 * admin a failed save for the common case (a typo, or `{{studentName}}`). It
 * deliberately mirrors the backend's single rule: any complete `{{...}}` naming
 * something not in the accepted set.
 *
 * ⚠️ Accepts the ALIASES too, or the chips would insert tokens this check then
 * rejects.
 */
const ACCEPTED_MERGE_TOKENS = new Set([
  'firstName',
  'lastName',
  'schoolName',
  'parentName',
  'recipientName',
]);

export function findUnknownMergeTokens(...sources: string[]): string[] {
  const found = new Set<string>();
  for (const source of sources) {
    for (const match of (source || '').matchAll(/\{\{([^}]*)\}\}/g)) {
      const raw = match[1].trim();

      // ⚠️ TEST THE RAW NAME FIRST, then strip tags only to build the readable
      // label. Stripping before the lookup HEALS a Quill-split token —
      // `{{first<strong>Name}}` becomes `firstName`, passes here, and is then
      // rejected by the server with a 422. That is precisely the round trip this
      // check exists to spare the admin, and the backend's own docblock warns
      // against exactly this ordering.
      if (ACCEPTED_MERGE_TOKENS.has(raw)) continue;

      const readable = raw.replace(/<[^>]*>/g, '').trim();
      found.add(
        ACCEPTED_MERGE_TOKENS.has(readable)
          ? `{{${readable}}} (broken by formatting — delete it and retype it)`
          : `{{${readable}}}`
      );
    }
  }
  return [...found];
}

interface RecipientPreviewDialogProps {
  open: boolean;
  onClose: () => void;
  customEmailId: string;
}

function RecipientPreviewDialog({ open, onClose, customEmailId }: RecipientPreviewDialogProps) {
  const { data: preview, isLoading, isError } = useRecipientPreview(customEmailId);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Recipient Preview</DialogTitle>
      <DialogContent>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : isError ? (
          <Alert severity="error">Failed to load recipient preview. Please try again.</Alert>
        ) : preview ? (
          <Stack spacing={3}>
            {/* Summary */}
            <Box>
              <Typography variant="h6" gutterBottom>
                Total Recipients: {preview.total}
              </Typography>
              <Stack direction="row" spacing={2}>
                <Chip
                  label={`Parents: ${preview.breakdown.byType.parents}`}
                  color="primary"
                  variant="outlined"
                />
                <Chip
                  label={`Teachers: ${preview.breakdown.byType.teachers}`}
                  color="secondary"
                  variant="outlined"
                />
              </Stack>
            </Box>

            {/* Breakdown by Age Group */}
            {Object.keys(preview.breakdown.byAgeGroup).length > 0 && (
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  By Age Group
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {Object.entries(preview.breakdown.byAgeGroup).map(([group, count]) => (
                    <Chip key={group} label={`${group}: ${count}`} size="small" />
                  ))}
                </Stack>
              </Box>
            )}

            {/* Breakdown by Instrument */}
            {Object.keys(preview.breakdown.byInstrument).length > 0 && (
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  By Instrument
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {Object.entries(preview.breakdown.byInstrument).map(([instrument, count]) => (
                    <Chip key={instrument} label={`${instrument}: ${count}`} size="small" />
                  ))}
                </Stack>
              </Box>
            )}

            {/* Sample Recipients */}
            {preview.sample && preview.sample.length > 0 && (
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Sample Recipients (first 10)
                </Typography>
                <List dense>
                  {preview.sample.slice(0, 10).map((recipient, index) => (
                    <ListItem key={index}>
                      <ListItemText
                        primary={`${recipient.firstName} ${recipient.lastName}`}
                        secondary={`${recipient.email} (${recipient.recipientType})`}
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}
          </Stack>
        ) : (
          <Typography color="text.secondary">No recipients found for current targeting.</Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

// ===========================================
// MAIN COMPONENT
// ===========================================

export default function CustomEmailEditorPage() {
  const navigate = useNavigate();
  const { schoolSlug } = useTenant();
  const { id } = useParams<{ id: string }>();
  const isNew = !id || id === 'new';
  const { enqueueSnackbar } = useSnackbar();

  // Form state
  const [title, setTitle] = useState('');
  const [subject, setSubject] = useState('');
  const [preheader, setPreheader] = useState('');
  const [content, setContent] = useState('');

  // Cursor targets for the merge-field chips. The body is Quill, so it needs the
  // editor's own imperative handle — a DOM selection range cannot insert into it.
  const subjectRef = useRef<HTMLInputElement>(null);
  const preheaderRef = useRef<HTMLInputElement>(null);
  const bodyEditorRef = useRef<RichTextEditorHandle>(null);
  const [sendToAll, setSendToAll] = useState(true);
  const [targetTeachers, setTargetTeachers] = useState(false);
  const [targetAgeGroups, setTargetAgeGroups] = useState<string[]>([]);
  const [targetProgramLevels, setTargetProgramLevels] = useState<string[]>([]);
  const [targetInstruments, setTargetInstruments] = useState<string[]>([]);
  // Classes and families are stored as IDs (unlike age groups / program levels
  // / instruments, which are stored by NAME), so these hold ids and the picker
  // maps them to labels for display.
  const [targetClasses, setTargetClasses] = useState<string[]>([]);
  const [targetFamilies, setTargetFamilies] = useState<string[]>([]);
  const [minAge, setMinAge] = useState<number | ''>('');
  const [maxAge, setMaxAge] = useState<number | ''>('');

  // Dialog state
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [testEmailOpen, setTestEmailOpen] = useState(false);
  const [recipientPreviewOpen, setRecipientPreviewOpen] = useState(false);
  const [deleteAttachmentId, setDeleteAttachmentId] = useState<string | null>(null);

  // Queries and mutations
  const { data: customEmail, isLoading } = useCustomEmail(id || '');
  const createMutation = useCreateCustomEmail();
  const updateMutation = useUpdateCustomEmail();
  const scheduleMutation = useScheduleCustomEmail();
  const sendMutation = useSendCustomEmail();
  const [sendConfirmOpen, setSendConfirmOpen] = useState(false);

  // Live recipient count for the send confirmation, mirroring the list page.
  // `totalRecipients` on the record is a STORED column written only AFTER a send
  // completes, so it is always 0 on a DRAFT — reading it here would tell the admin
  // they are about to email nobody. Guarded on the dialog being open, so it costs
  // nothing until an irreversible action is actually being considered.
  const {
    data: sendPreview,
    isLoading: isLoadingSendPreview,
    isError: sendPreviewError,
  } = useRecipientPreview(sendConfirmOpen && id ? id : '');
  const duplicateMutation = useDuplicateCustomEmail();

  /**
   * Duplicate is the real "edit this" path for a sent campaign: it clones the
   * content into a fresh DRAFT, which IS editable. Navigating straight to the
   * copy means the admin lands where they wanted to be — otherwise they get a
   * success toast and are left on a page they still cannot change.
   */
  const handleDuplicate = async () => {
    if (!id) return;
    try {
      const copy = await duplicateMutation.mutateAsync(id);
      enqueueSnackbar('Campaign duplicated — editing the copy.', { variant: 'success' });
      navigate(`/${schoolSlug}/admin/custom-emails/${copy.id}`);
    } catch (error) {
      // ⚠️ Surface the server's message. Duplicating a campaign that predates
      // merge-field validation fails with a 422 NAMING the bad token, and a
      // generic "Could not duplicate" throws that away — leaving the admin with
      // a button that fails for no stated reason on an otherwise fine campaign.
      enqueueSnackbar(`Could not duplicate this campaign: ${saveErrorMessage(error)}`, {
        variant: 'error',
      });
    }
  };
  const testEmailMutation = useSendTestEmail();

  // Attachment queries and mutations
  const { data: attachments = [], isLoading: attachmentsLoading } = useAttachments(id || '');
  const uploadAttachmentMutation = useUploadAttachment();
  const deleteAttachmentMutation = useDeleteAttachment();

  // Fetch targeting options from admin data
  const { data: ageGroupsData } = useAgeGroups();
  const { data: programLevelsData } = useProgramLevels();
  const { data: instrumentsData } = useInstruments();
  const { data: lessonsData, isLoading: isLoadingLessons } = useLessons();
  const { data: familiesData, isLoading: isLoadingFamilies } = useFamilies();

  // Extract option names for autocomplete
  const ageGroupOptions = ageGroupsData?.map((ag) => ag.name) || [];
  const programLevelOptions = programLevelsData?.map((pl) => pl.name) || [];
  const instrumentOptions = instrumentsData?.map((i) => i.name) || [];

  type IdOption = { id: string; label: string };
  const classOptions: IdOption[] = (lessonsData ?? []).map((l) => ({ id: l.id, label: l.name }));
  const familyOptions: IdOption[] = (familiesData ?? []).map((f) => ({ id: f.id, label: f.name }));

  // Load custom email data
  useEffect(() => {
    if (customEmail) {
      setTitle(customEmail.title);
      setSubject(customEmail.subject);
      setPreheader(customEmail.preheader || '');
      setContent(customEmail.content);
      setSendToAll(customEmail.sendToAll);
      setTargetTeachers(customEmail.targetTeachers);
      setTargetAgeGroups(customEmail.targetAgeGroups);
      setTargetProgramLevels(customEmail.targetProgramLevels);
      setTargetInstruments(customEmail.targetInstruments);
      setTargetClasses(customEmail.targetClasses ?? []);
      setTargetFamilies(customEmail.targetFamilies ?? []);
      setMinAge(customEmail.minAge ?? '');
      setMaxAge(customEmail.maxAge ?? '');
    }
  }, [customEmail]);

  const handleSave = async () => {
    // Catch a bad token before the round trip. The backend still rejects it
    // (it is the authority), but a typo should not cost a failed request and an
    // error banner when we can name it immediately.
    const unknown = findUnknownMergeTokens(content, subject, preheader);
    if (unknown.length > 0) {
      enqueueSnackbar(
        `Unknown merge ${unknown.length === 1 ? 'field' : 'fields'}: ${unknown.join(', ')}. ` +
          `Use the Personalise chips — an unrecognised field is sent as blank text.`,
        { variant: 'error' }
      );
      return;
    }

    try {
      const data: CreateCustomEmailData | UpdateCustomEmailData = {
        title,
        subject,
        preheader: preheader || undefined,
        content,
        sendToAll,
        targetTeachers,
        targetAgeGroups,
        targetProgramLevels,
        targetClasses,
        targetFamilies,
        targetInstruments,
        minAge: minAge !== '' ? Number(minAge) : undefined,
        maxAge: maxAge !== '' ? Number(maxAge) : undefined,
      };

      if (isNew) {
        const created = await createMutation.mutateAsync(data as CreateCustomEmailData);
        enqueueSnackbar('Email created successfully', { variant: 'success' });
        navigate(`/${schoolSlug}/admin/custom-emails/${created.id}`, { replace: true });
      } else if (id) {
        await updateMutation.mutateAsync({ customEmailId: id, data: data as UpdateCustomEmailData });
        enqueueSnackbar('Email saved successfully', { variant: 'success' });
      }
    } catch (err) {
      console.error('Failed to save custom email:', err);
      enqueueSnackbar('Failed to save email', { variant: 'error' });
    }
  };

  const handleSchedule = async (scheduledFor: string) => {
    if (!id) return;
    try {
      await scheduleMutation.mutateAsync({ customEmailId: id, data: { scheduledFor } });
      setScheduleOpen(false);
    } catch (err) {
      // ⚠️ Must be VISIBLE. Scheduling now refuses a campaign carrying an
      // unresolvable merge field (422, naming it), and a console-only error
      // would leave the dialog open with no explanation — the admin would just
      // see the Schedule button do nothing.
      console.error('Failed to schedule email:', err);
      enqueueSnackbar(`Could not schedule: ${saveErrorMessage(err)}`, { variant: 'error' });
    }
  };

  const handleSend = async () => {
    if (!id) return;
    try {
      await sendMutation.mutateAsync(id);
      setSendConfirmOpen(false);
    } catch (err) {
      // Deliberately leave the dialog OPEN on failure — closing it would hide the
      // error banner behind the page and make a failed send look like nothing
      // happened at all.
      console.error('Failed to send email:', err);
    }
  };

  /**
   * Insert a merge field at the cursor.
   *
   * ⚠️ The body needs Quill's OWN insert. A `document.getElementById` +
   * `selectionStart` splice (what the SMS editor does) works on a plain
   * textarea and does nothing useful on a Quill surface — it would either no-op
   * or corrupt the editor's internal model. `RichTextEditor` exposes
   * `insertText` for exactly this; the subject and preheader are ordinary
   * inputs and take the selection splice.
   *
   * `preventDefault` keeps the chip click from stealing focus before we read
   * the selection.
   */
  const insertMergeField = (
    e: React.MouseEvent,
    placeholder: string,
    target: 'subject' | 'preheader' | 'body'
  ) => {
    // Both rules live in `utils/mergeFieldInsert` so this page and the
    // email-sequence editor cannot drift apart. See there for WHY each exists.
    if (isDuplicateMouseActivation(e)) return;

    e.preventDefault();

    if (target === 'body') {
      bodyEditorRef.current?.insertText(placeholder);
      return;
    }

    const input = target === 'subject' ? subjectRef.current : preheaderRef.current;
    const setValue = target === 'subject' ? setSubject : setPreheader;

    if (!input) {
      setValue((prev) => prev + placeholder);
      return;
    }

    const { value, caret } = insertAtSelectionEnd(input, placeholder);
    setValue(value);

    // Put the caret after what we just inserted, so a second chip does not land
    // inside the first.
    requestAnimationFrame(() => {
      input.focus();
      input.setSelectionRange(caret, caret);
    });
  };

  const handleTestEmail = async (emailAddress: string) => {
    if (!id) return;
    try {
      await testEmailMutation.mutateAsync({ customEmailId: id, data: { email: emailAddress } });
      setTestEmailOpen(false);
    } catch (err) {
      console.error('Failed to send test email:', err);
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!id || !event.target.files?.length) return;
    const file = event.target.files[0];
    try {
      await uploadAttachmentMutation.mutateAsync({ customEmailId: id, file });
    } catch (err) {
      console.error('Failed to upload attachment:', err);
    }
    // Reset input so same file can be uploaded again
    event.target.value = '';
  };

  const handleDeleteAttachment = async () => {
    if (!id || !deleteAttachmentId) return;
    try {
      await deleteAttachmentMutation.mutateAsync({ customEmailId: id, attachmentId: deleteAttachmentId });
      setDeleteAttachmentId(null);
    } catch (err) {
      console.error('Failed to delete attachment:', err);
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const isSaving = createMutation.isPending || updateMutation.isPending;
  const status: CustomEmailStatus = customEmail?.status || 'DRAFT';

  /**
   * Does the form differ from the SAVED record?
   *
   * The rule itself lives in `customEmailDirty.ts` so the page and its tests
   * share ONE implementation — see the reasoning there. It is safety-critical:
   * Send passes only the campaign id, so a false "clean" mails the previously
   * saved version to the whole audience.
   */
  const isDirty = isCustomEmailDirty(
    {
      title,
      subject,
      preheader,
      content,
      sendToAll,
      targetTeachers,
      targetAgeGroups,
      targetProgramLevels,
      targetInstruments,
      targetClasses,
      targetFamilies,
      minAge,
      maxAge,
    },
    customEmail,
    isNew
  );
  const canSchedule = status === 'DRAFT' && !isNew;
  const canSend = status === 'DRAFT' && !isNew;

  /**
   * A SENT or SENDING campaign is an immutable record of what went out.
   *
   * The backend enforces this (`updateCustomEmail` 400s on both), but the
   * editor used to render Save unconditionally — so saving a sent campaign
   * failed with a raw "Request failed with status code 400" after the admin had
   * already made their edits. The whole form looked live and editable on a
   * record that could never be saved.
   *
   * Test is gated too: a test send re-renders the CURRENT form state, which on
   * a sent campaign is content that may differ from what recipients actually
   * received — misleading on the one page where the record is supposed to be
   * authoritative.
   */
  const isImmutable = status === 'SENT' || status === 'SENDING';

  // Covers tab-close, reload, browser Back and a typed URL — the exits this
  // editor had NO guard for at all (its Back is a bare `Link`). In-app sidebar
  // navigation is still unguarded here; see the hook for why `useBlocker` is
  // unavailable in this app.
  // ⚠️ Gated on `!isImmutable`: a SENT or SENDING campaign cannot be edited, so
  // its fields can differ from the record without anything being LOSABLE, and
  // prompting there would be a warning about nothing.
  useUnsavedChangesWarning(isDirty && !isImmutable);
  const canTest = !isNew && !isImmutable;
  const canSave = !isImmutable;

  // ⚠️ The loading return comes AFTER every hook (useUnsavedChangesWarning above). Saving a NEW
  // campaign moves this same instance from /new to /:id, so one render loads; an early return
  // above a hook made that render call fewer hooks and the next one more: React #310 on every
  // first save (staging walkthrough 13/10/2026, in production since 0dafa786).
  if (!isNew && isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader
        title={isNew ? 'Create Email Campaign' : 'Edit Email Campaign'}
        subtitle={customEmail?.title || 'New email campaign'}
        action={
          <Stack direction="row" spacing={1}>
            <Button startIcon={<BackIcon />} component={Link} to={`/${schoolSlug}/admin/custom-emails`}>
              Back
            </Button>
            {canTest && (
              <Button
                startIcon={<TestEmailIcon />}
                onClick={() => setTestEmailOpen(true)}
                disabled={testEmailMutation.isPending}
              >
                Test
              </Button>
            )}
            {canSchedule && (
              <Button
                startIcon={<ScheduleIcon />}
                onClick={() => setScheduleOpen(true)}
                disabled={scheduleMutation.isPending || isDirty}
              >
                Schedule
              </Button>
            )}
            {canSend && (
              <Button
                variant="outlined"
                color="success"
                startIcon={<SendIcon />}
                onClick={() => setSendConfirmOpen(true)}
                disabled={sendMutation.isPending || isDirty}
              >
                Send Now
              </Button>
            )}
            {/* A sent campaign gets the actions that actually apply to it —
                mirroring the list page's menu — instead of a Save button that
                can only ever 400. Duplicate is the real "edit this" path:
                it clones to a fresh DRAFT. */}
            {isImmutable && (
              <Button
                startIcon={<DuplicateIcon />}
                onClick={handleDuplicate}
                disabled={duplicateMutation.isPending}
              >
                Duplicate
              </Button>
            )}
            {isImmutable && (
              <Button
                variant="contained"
                startIcon={<AnalyticsIcon />}
                component={Link}
                to={`/${schoolSlug}/admin/custom-emails/${id}/analytics`}
              >
                Analytics
              </Button>
            )}
            {canSave && (
              <Button
                variant="contained"
                startIcon={isSaving ? <CircularProgress size={20} /> : <SaveIcon />}
                onClick={handleSave}
                disabled={isSaving || !title.trim() || !subject.trim() || !content.trim()}
              >
                Save
              </Button>
            )}
          </Stack>
        }
      />

      {/* Info alert for new emails */}
      {isNew && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Save your email first to enable testing, scheduling, and sending options.
        </Alert>
      )}

      {/* Error alerts */}
      {createMutation.isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to create email: {saveErrorMessage(createMutation.error)}
        </Alert>
      )}
      {updateMutation.isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to update email: {saveErrorMessage(updateMutation.error)}
        </Alert>
      )}
      {/* Say WHY the form cannot be saved, rather than letting a disabled Save
          button look like a bug. Without this the page reads as broken: every
          field still accepts typing (the record is worth reading in full), but
          nothing can be persisted. */}
      {isImmutable && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {status === 'SENDING'
            ? customEmail?.sendState === 'QUEUED'
              ? 'This campaign is queued and will start sending shortly, so it cannot be edited. Its content is locked from the moment it is queued.'
              : 'This campaign is currently being sent, so it cannot be edited. Its content is locked until the send finishes.'
            : 'This campaign has been sent, so it is now a read-only record of what recipients received. Use Duplicate to create an editable copy.'}
        </Alert>
      )}
      {/* Send and Schedule pass only the campaign id — the server mails whatever
          was last SAVED. Editing and clicking Send Now without saving would mail
          the OLD version to the whole audience, silently, with no way to recall
          it. Both are disabled until the changes are saved. */}
      {isDirty && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          You have unsaved changes. Save them before sending or scheduling —
          otherwise recipients would receive the previously saved version.
        </Alert>
      )}
      {sendMutation.isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {/* ⚠️ The server's message, not axios's status line. Send re-validates
              merge fields (a legacy campaign saved before that guard can still
              carry a bad token), and the 422 NAMES the field — "Request failed
              with status code 422" would tell the admin nothing. */}
          Failed to send email: {saveErrorMessage(sendMutation.error)}
        </Alert>
      )}
      {sendMutation.isSuccess && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {/* NOT "sent": the campaign is queued and sends in the background at
              the school's sending rate. Claiming it is sent the instant the
              request returns is the one thing this whole change exists to
              stop being a lie. */}
          Campaign queued — it sends in the background. You can leave this page.
        </Alert>
      )}
      {testEmailMutation.isSuccess && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Test email sent successfully!
        </Alert>
      )}

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
        {/* Main content */}
        <Box sx={{ flex: 2 }}>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Email Details
            </Typography>
            <Stack spacing={2}>
              <TextField
                label="Internal Title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                fullWidth
                required
                placeholder="Internal name for this email (not sent to recipients)"
                helperText="This is for your reference only"
                disabled={isImmutable}
              />
              <Box>
                <TextField
                  label="Email Subject"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  inputRef={subjectRef}
                  fullWidth
                  required
                  placeholder="Subject line that recipients will see"
                  inputProps={{ maxLength: 200 }}
                  helperText={`${subject.length}/200 characters`}
                  disabled={isImmutable}
                />
                <MergeFieldChips fields={MERGE_FIELDS} target="subject" onInsert={insertMergeField} disabled={isImmutable} />
              </Box>
              <Box>
                <TextField
                  label="Preheader (optional)"
                  value={preheader}
                  onChange={(e) => setPreheader(e.target.value)}
                  inputRef={preheaderRef}
                  fullWidth
                  placeholder="Preview text shown in inbox"
                  inputProps={{ maxLength: 150 }}
                  helperText={`${preheader.length}/150 characters - This appears after the subject in email previews`}
                  disabled={isImmutable}
                />
                <MergeFieldChips fields={MERGE_FIELDS} target="preheader" onInsert={insertMergeField} disabled={isImmutable} />
              </Box>
            </Stack>
          </Paper>

          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Email Content
            </Typography>
            <MergeFieldChips fields={MERGE_FIELDS} target="body" onInsert={insertMergeField} disabled={isImmutable} />
            <RichTextEditor
              ref={bodyEditorRef}
              value={content}
              onChange={setContent}
              placeholder="Start with your own greeting, e.g. Hi {{parentName}},"
              height={450}
              /* ⚠️ This wording is load-bearing. The old text — "Content will be
                 wrapped in your school's email template" — read as branding and
                 gave no hint that a greeting was being injected above the body,
                 which is exactly why MNM wrote their own and every recipient got
                 TWO. Say plainly that no greeting is added. */
              helperText="Write your own greeting — nothing is added above your content. Use the chips to personalise it. Your content is wrapped in the school's branded header and footer."
              disabled={isImmutable}
            />
          </Paper>
        </Box>

        {/* Sidebar */}
        <Box sx={{ flex: 1, minWidth: 300 }}>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Targeting
            </Typography>
            <Stack spacing={2}>
              <FormControlLabel
                control={
                  <Switch
                    checked={sendToAll}
                    disabled={isImmutable}
                    onChange={(e) => setSendToAll(e.target.checked)}
                  />
                }
                label="Send to all parents"
              />

              <FormControlLabel
                control={
                  <Switch
                    checked={targetTeachers}
                    disabled={isImmutable}
                    onChange={(e) => setTargetTeachers(e.target.checked)}
                  />
                }
                label="Include teachers"
              />

              {!sendToAll && (
                <>
                  <Divider />
                  <Typography variant="subtitle2" color="text.secondary">
                    Target specific groups:
                  </Typography>

                  <Autocomplete
                    multiple
                    options={ageGroupOptions}
                    value={targetAgeGroups}
                    onChange={(_, value) => setTargetAgeGroups(value)}
                    disabled={isImmutable}
                    renderInput={(params) => (
                      <TextField {...params} label="Age Groups" size="small" />
                    )}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip
                          {...getTagProps({ index })}
                          key={option}
                          label={option}
                          size="small"
                        />
                      ))
                    }
                  />

                  <Autocomplete
                    multiple
                    options={programLevelOptions}
                    value={targetProgramLevels}
                    onChange={(_, value) => setTargetProgramLevels(value)}
                    disabled={isImmutable}
                    renderInput={(params) => (
                      <TextField {...params} label="Program Levels" size="small" />
                    )}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip
                          {...getTagProps({ index })}
                          key={option}
                          label={option}
                          size="small"
                        />
                      ))
                    }
                  />

                  <Autocomplete
                    multiple
                    options={instrumentOptions}
                    value={targetInstruments}
                    onChange={(_, value) => setTargetInstruments(value)}
                    disabled={isImmutable}
                    renderInput={(params) => (
                      <TextField {...params} label="Instruments" size="small" />
                    )}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip
                          {...getTagProps({ index })}
                          key={option}
                          label={option}
                          size="small"
                        />
                      ))
                    }
                  />

                  {/* Classes and families target by ID, not by name.
                      The three pickers above store NAMES (age group / program
                      level / instrument are matched by name server-side), but
                      these two store ids — so the option objects carry {id,
                      label} and only the ids go into the payload. */}
                  <Autocomplete
                    multiple
                    options={classOptions}
                    loading={isLoadingLessons}
                    value={classOptions.filter((o) => targetClasses.includes(o.id))}
                    getOptionLabel={(o) => o.label}
                    isOptionEqualToValue={(o, v) => o.id === v.id}
                    onChange={(_, value) => setTargetClasses(value.map((o) => o.id))}
                    disabled={isImmutable}
                    renderInput={(params) => (
                      <TextField {...params} label="Classes" size="small" />
                    )}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip
                          {...getTagProps({ index })}
                          key={option.id}
                          label={option.label}
                          size="small"
                        />
                      ))
                    }
                  />

                  <Autocomplete
                    multiple
                    options={familyOptions}
                    loading={isLoadingFamilies}
                    value={familyOptions.filter((o) => targetFamilies.includes(o.id))}
                    getOptionLabel={(o) => o.label}
                    isOptionEqualToValue={(o, v) => o.id === v.id}
                    onChange={(_, value) => setTargetFamilies(value.map((o) => o.id))}
                    disabled={isImmutable}
                    renderInput={(params) => (
                      <TextField {...params} label="Families" size="small" />
                    )}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip
                          {...getTagProps({ index })}
                          key={option.id}
                          label={option.label}
                          size="small"
                        />
                      ))
                    }
                  />

                  <Divider />
                  <Typography variant="subtitle2" color="text.secondary">
                    Age range filter:
                  </Typography>

                  <Stack direction="row" spacing={2}>
                    <TextField
                      label="Min Age"
                      type="number"
                      value={minAge}
                      disabled={isImmutable}
                      onChange={(e) => setMinAge(e.target.value ? Number(e.target.value) : '')}
                      size="small"
                      inputProps={{ min: 0, max: 100 }}
                    />
                    <TextField
                      label="Max Age"
                      type="number"
                      value={maxAge}
                      disabled={isImmutable}
                      onChange={(e) => setMaxAge(e.target.value ? Number(e.target.value) : '')}
                      size="small"
                      inputProps={{ min: 0, max: 100 }}
                    />
                  </Stack>
                </>
              )}

              {!isNew && (
                <Button
                  variant="outlined"
                  startIcon={<PeopleIcon />}
                  onClick={() => setRecipientPreviewOpen(true)}
                  fullWidth
                >
                  Preview Recipients
                </Button>
              )}
            </Stack>
          </Paper>

          {/* Attachments */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Attachments
            </Typography>
            {isNew ? (
              <Typography variant="body2" color="text.secondary">
                Save the email first to add attachments.
              </Typography>
            ) : (
              <Stack spacing={2}>
                {/* Upload progress */}
                {uploadAttachmentMutation.isPending && (
                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      Uploading...
                    </Typography>
                    <LinearProgress />
                  </Box>
                )}

                {/* Upload error */}
                {uploadAttachmentMutation.isError && (
                  <Alert severity="error" sx={{ py: 0.5 }}>
                    {uploadAttachmentMutation.error instanceof Error
                      ? uploadAttachmentMutation.error.message
                      : 'Failed to upload attachment'}
                  </Alert>
                )}

                {/* Attachment list */}
                {attachmentsLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                    <CircularProgress size={24} />
                  </Box>
                ) : attachments.length > 0 ? (
                  <List dense disablePadding>
                    {attachments.map((attachment) => (
                      <ListItem
                        key={attachment.id}
                        sx={{
                          bgcolor: 'action.hover',
                          borderRadius: 1,
                          mb: 0.5,
                          pr: 6,
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <FileIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText
                          primary={attachment.fileName}
                          secondary={formatFileSize(attachment.fileSize)}
                          primaryTypographyProps={{ variant: 'body2', noWrap: true }}
                          secondaryTypographyProps={{ variant: 'caption' }}
                        />
                        <ListItemSecondaryAction>
                          <Tooltip title="Delete attachment">
                            <IconButton
                              edge="end"
                              size="small"
                              onClick={() => setDeleteAttachmentId(attachment.id)}
                              disabled={deleteAttachmentMutation.isPending}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </ListItemSecondaryAction>
                      </ListItem>
                    ))}
                  </List>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No attachments yet.
                  </Typography>
                )}

                {/* Upload button */}
                {status === 'DRAFT' && (
                  <>
                    <input
                      accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.jpg,.jpeg,.png,.gif,.webp"
                      id="attachment-upload"
                      type="file"
                      style={{ display: 'none' }}
                      onChange={handleFileUpload}
                      disabled={uploadAttachmentMutation.isPending || attachments.length >= 5}
                    />
                    <label htmlFor="attachment-upload">
                      <Button
                        variant="outlined"
                        component="span"
                        startIcon={<AttachFileIcon />}
                        fullWidth
                        disabled={uploadAttachmentMutation.isPending || attachments.length >= 5}
                      >
                        {attachments.length >= 5 ? 'Max 5 attachments' : 'Add Attachment'}
                      </Button>
                    </label>
                    <Typography variant="caption" color="text.secondary">
                      Max 10MB per file. PDF, Word, Excel, PowerPoint, text, CSV, and images.
                    </Typography>
                  </>
                )}
              </Stack>
            )}
          </Paper>

          {/* Status info */}
          {customEmail && (
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Status
              </Typography>
              <Stack spacing={1}>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">
                    Status
                  </Typography>
                  <Chip
                    label={
                      status === 'SENDING' && customEmail?.sendState === 'QUEUED' ? 'QUEUED' : status
                    }
                    size="small"
                    color={
                      status === 'SENT'
                        ? 'success'
                        : status === 'SENDING'
                          ? 'warning'
                          : status === 'SCHEDULED'
                            ? 'info'
                            : status === 'FAILED'
                              ? 'error'
                              : 'default'
                    }
                  />
                </Stack>
                {customEmail.scheduledFor && (
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">
                      Scheduled For
                    </Typography>
                    <Typography variant="body2">
                      {new Date(customEmail.scheduledFor).toLocaleString()}
                    </Typography>
                  </Stack>
                )}
                {customEmail.sentAt && (
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">
                      Sent At
                    </Typography>
                    <Typography variant="body2">
                      {new Date(customEmail.sentAt).toLocaleString()}
                    </Typography>
                  </Stack>
                )}
                <Divider />
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">
                    Recipients
                  </Typography>
                  <Typography variant="body2">{customEmail.totalRecipients}</Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">
                    Sent
                  </Typography>
                  <Typography variant="body2">{customEmail.sentCount}</Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">
                    Delivered
                  </Typography>
                  <Typography variant="body2">{customEmail.deliveredCount}</Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">
                    Opened
                  </Typography>
                  <Typography variant="body2">
                    {customEmail.openedCount}
                    {customEmail.sentCount > 0 &&
                      ` (${((customEmail.openedCount / customEmail.sentCount) * 100).toFixed(1)}%)`}
                  </Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">
                    Clicked
                  </Typography>
                  <Typography variant="body2">
                    {customEmail.clickedCount}
                    {customEmail.sentCount > 0 &&
                      ` (${((customEmail.clickedCount / customEmail.sentCount) * 100).toFixed(1)}%)`}
                  </Typography>
                </Stack>
              </Stack>
            </Paper>
          )}
        </Box>
      </Stack>

      {/* Dialogs */}
      <ScheduleDialog
        open={scheduleOpen}
        onClose={() => setScheduleOpen(false)}
        onSchedule={handleSchedule}
        isScheduling={scheduleMutation.isPending}
      />

      <TestEmailDialog
        open={testEmailOpen}
        onClose={() => setTestEmailOpen(false)}
        onSend={handleTestEmail}
        isSending={testEmailMutation.isPending}
        defaultEmail={customEmail?.author?.email || ''}
      />

      {id && id !== 'new' && (
        <RecipientPreviewDialog
          open={recipientPreviewOpen}
          onClose={() => setRecipientPreviewOpen(false)}
          customEmailId={id}
        />
      )}

      {/* Send confirmation — the editor's "Send Now" previously fired an
          irreversible bulk mail-out with NO confirmation at all, on the page an
          admin is most likely to be on. Mirrors the list page exactly: the count
          is resolved live, and sending is BLOCKED until it does, because
          confirming against an unknown audience is what this dialog exists to
          prevent. */}
      <ConfirmDialog
        open={sendConfirmOpen}
        title="Send Email Campaign"
        message={
          isLoadingSendPreview
            ? `Checking how many recipients "${title}" will reach…`
            : sendPreviewError
              ? `Could not work out how many recipients "${title}" will reach. Check the audience with Preview Recipients before sending.`
              : `Send "${title}" to ${
                  sendPreview?.total ?? 0
                } recipients? It is queued immediately and sends in the background at the school's sending rate, so it may take a while to finish. There is no way to stop it once queued.`
        }
        confirmLabel="Send"
        confirmColor="primary"
        onConfirm={handleSend}
        onCancel={() => setSendConfirmOpen(false)}
        loading={sendMutation.isPending || isLoadingSendPreview}
        confirmDisabled={!!sendPreviewError || (sendPreview?.total ?? 0) === 0}
      />

      {/* Delete Attachment Confirmation Dialog */}
      <Dialog
        open={!!deleteAttachmentId}
        onClose={() => setDeleteAttachmentId(null)}
      >
        <DialogTitle>Delete Attachment</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this attachment? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteAttachmentId(null)} disabled={deleteAttachmentMutation.isPending}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteAttachment}
            disabled={deleteAttachmentMutation.isPending}
          >
            {deleteAttachmentMutation.isPending ? <CircularProgress size={20} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
